import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  AdjustmentAidFormValues,
  AdjustmentAidFullTexts,
  AdjustmentAidParticipant,
  ApplicationType,
} from '../../types/Application/adjustmentAid';
import {
  AdjustmentAidApplicationType,
  AdjustmentAidMyRoleType,
  AdjustmentAidUnionRoleType,
  MoreApplications,
} from '../../constants/Enum';
import { tsl_projektdeltagande_tsl_anstallningsform as EmploymentFormEnum } from '../../types/Enums/Generated/tsl_projektdeltagande_tsl_anstallningsform';
import { ValidatedField } from '../../types/validation';
import { validateField } from '../../validation';
import {
  ApplicationFile,
  DropdownAlternative,
  Worksite,
} from '../../types/Application/application';
import { initialState } from './adjustmentAidReducer';

const fullTextsMapping = {
  [AdjustmentAidApplicationType.Dismissed]: 'dismissedApplicationTexts',
  [AdjustmentAidApplicationType.Bankruptcy]: 'bankruptcyApplicationTexts',
  [AdjustmentAidApplicationType.Illness]: 'illnessApplicationTexts',
};

const adjustmentAidSlice = createSlice({
  name: 'adjustmentAid',
  initialState,
  reducers: {
    resetExistingAutomatedBankruptcyFlowState: (state) => {
      state.hasExistingAutomatedBankruptcyIncident = false;
      state.existingAutomatedBankruptcyIncidentId = undefined;
      state.existingAutomatedBankruptcyIncidentLookupFailed = false;
      state.isCheckingExistingAutomatedBankruptcyIncident = false;
      state.isExistingAutomatedBankruptcyIncidentFlowSuccess = false;
      state.isExistingAutomatedBankruptcyIncidentFlowSubmitting = false;
    },
    setStateApplicationType: (
      state,
      action: PayloadAction<ApplicationType>
    ) => {
      state.hasExistingAutomatedBankruptcyIncident = false;
      state.existingAutomatedBankruptcyIncidentId = undefined;
      state.existingAutomatedBankruptcyIncidentLookupFailed = false;
      state.isCheckingExistingAutomatedBankruptcyIncident = false;
      state.isExistingAutomatedBankruptcyIncidentFlowSuccess = false;
      state.isExistingAutomatedBankruptcyIncidentFlowSubmitting = false;

      if (action.payload === AdjustmentAidApplicationType.Bankruptcy) {
        // Clear companyContact fields if changing to bankruptcy applicatiom
        state.form.companyContactFirstname =
          initialState.form.companyContactFirstname;
        state.form.companyContactLastname =
          initialState.form.companyContactLastname;
        state.form.companyContactAddress =
          initialState.form.companyContactAddress;
        state.form.companyContactCity = initialState.form.companyContactCity;
        state.form.companyContactZipCode =
          initialState.form.companyContactZipCode;
        state.form.companyContactTelephone =
          initialState.form.companyContactTelephone;
        state.form.companyContactEmail = initialState.form.companyContactEmail;

        state.applicationType = action.payload;
      } else if (action.payload === AdjustmentAidApplicationType.Illness) {
        // Clear terminatd participants if more than 1
        if (state.terminatedParticipants.length > 1) {
          state.terminatedParticipants = initialState.terminatedParticipants;
        }
        state.applicationType = action.payload;
        state.moreApplications = false;
        state.form.moreApplicationsRadioButtons.isValid = true;
        state.form.moreApplicationsRadioButtons.isDirty = true;
        state.form.moreApplicationsRadioButtons.value = 'no';
      } else {
        state.applicationType = action.payload;
      }
    },
    validateAndSetFormValueAdjustmentAid: (
      state,
      action: PayloadAction<ValidatedField>
    ) => {
      if (action.payload.validationType === 'FILE') {
        const { files, ...payloadWithoutFiles } = action.payload;
        state.form = {
          ...state.form,
          [action.payload.name]: {
            ...state.form[action.payload.name as keyof AdjustmentAidFormValues],
            payloadWithoutFiles,
            isValid: validateField({
              ...state.form[
                action.payload.name as keyof AdjustmentAidFormValues
              ],
              ...action.payload,
            }),
          },
        };
      } else {
        state.form = {
          ...state.form,
          [action.payload.name]: {
            ...state.form[action.payload.name as keyof AdjustmentAidFormValues],
            ...action.payload,
            isValid: validateField({
              ...state.form[
                action.payload.name as keyof AdjustmentAidFormValues
              ],
              ...action.payload,
            }),
          },
        };
      }
    },
    setCheckingExistingAutomatedBankruptcyIncident: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isCheckingExistingAutomatedBankruptcyIncident = action.payload;
      if (action.payload) {
        state.existingAutomatedBankruptcyIncidentLookupFailed = false;
      }
    },
    setExistingAutomatedBankruptcyIncidentResult: (
      state,
      action: PayloadAction<{
        incidentId?: string;
        hasExistingIncident: boolean;
      }>
    ) => {
      state.existingAutomatedBankruptcyIncidentId = action.payload.incidentId;
      state.hasExistingAutomatedBankruptcyIncident =
        action.payload.hasExistingIncident;
      state.isCheckingExistingAutomatedBankruptcyIncident = false;
      state.existingAutomatedBankruptcyIncidentLookupFailed = false;
      state.isExistingAutomatedBankruptcyIncidentFlowSuccess = false;
      state.isExistingAutomatedBankruptcyIncidentFlowSubmitting = false;
    },
    setExistingAutomatedBankruptcyIncidentLookupFailed: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.existingAutomatedBankruptcyIncidentLookupFailed = action.payload;
      state.isCheckingExistingAutomatedBankruptcyIncident = false;
      state.isExistingAutomatedBankruptcyIncidentFlowSuccess = false;
      state.isExistingAutomatedBankruptcyIncidentFlowSubmitting = false;
      if (action.payload) {
        state.hasExistingAutomatedBankruptcyIncident = false;
        state.existingAutomatedBankruptcyIncidentId = undefined;
      }
    },
    setExistingAutomatedBankruptcyIncidentFlowSuccess: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isExistingAutomatedBankruptcyIncidentFlowSuccess = action.payload;
    },
    setExistingAutomatedBankruptcyIncidentFlowSubmitting: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isExistingAutomatedBankruptcyIncidentFlowSubmitting =
        action.payload;
    },
    setWorksite: (state, action: PayloadAction<Worksite | undefined>) => {
      if (action.payload !== undefined) {
        state.form.worksiteName.isValid = true;
        state.form.worksiteAddress.isValid = true;
        state.form.worksiteZipCode.isValid = true;
        state.form.worksiteCity.isValid = true;
        state.form.worksiteTelephone.isValid = true;
        state.worksite = action.payload;
      } else {
        state.form.worksiteName.isValid = false;
        state.form.worksiteAddress.isValid = false;
        state.form.worksiteZipCode.isValid = false;
        state.form.worksiteCity.isValid = false;
        state.form.worksiteTelephone.isValid = false;
        state.worksite = initialState.worksite;
      }
    },
    clearDidNotFindWorksiteFields: (state, action: PayloadAction<boolean>) => {
      state.form.worksiteName = initialState.form.worksiteName;
      state.form.worksiteAddress = initialState.form.worksiteAddress;
      state.form.worksiteCity = initialState.form.worksiteCity;
      state.form.worksiteZipCode = initialState.form.worksiteZipCode;
      state.form.worksiteTelephone = initialState.form.worksiteTelephone;
      if (action.payload) {
        state.form.worksiteDropdown = initialState.form.worksiteDropdown;
      }
    },
    setFullTexts: (state, action: PayloadAction<AdjustmentAidFullTexts>) => {
      if (state.applicationType) {
        const key = fullTextsMapping[state.applicationType];
        if (key) {
          return {
            ...state,
            fullTexts: action.payload[key as keyof AdjustmentAidFullTexts],
          };
        }
      }
    },
    addTerminatedParticipant: (
      state,
      action: PayloadAction<EmploymentFormEnum>
    ) => {
      const terminatedPerson = {
        nodeId: new Date().toString(),
        employedFrom: state.form.personEmployedFrom.value,
        employedTo: state.form.personEmployedTo.value,
        noticeDate: state.form.personNoticeDate.value,
        employmentType: action.payload,
        contact: {
          socialSecurityNumber: state.form.personPersonalnumber.value,
          firstname: state.form.personFirstname.value,
          lastname: state.form.personLastname.value,
          email: state.form.personEmail.value,
          phone: state.form.personTelephone.value,
        },
      } as AdjustmentAidParticipant;
      return {
        ...state,
        terminatedParticipants: [
          ...state.terminatedParticipants,
          terminatedPerson,
        ],
      };
    },
    saveEditedParticipant: (
      state,
      action: PayloadAction<{
        nodeId: string;
        employmentForm: EmploymentFormEnum;
      }>
    ) => {
      const updatedParticipantData = {
        nodeId: action.payload.nodeId,
        employedFrom: state.form.personEmployedFrom.value,
        employedTo: state.form.personEmployedTo.value,
        noticeDate: state.form.personNoticeDate.value,
        employmentType: action.payload.employmentForm,
        contact: {
          socialSecurityNumber: state.form.personPersonalnumber.value,
          firstname: state.form.personFirstname.value,
          lastname: state.form.personLastname.value,
          email: state.form.personEmail.value,
          phone: state.form.personTelephone.value,
        },
      } as AdjustmentAidParticipant;

      return {
        ...state,
        terminatedParticipants: state.terminatedParticipants.map(
          (participant) =>
            participant.nodeId === action.payload.nodeId
              ? { ...updatedParticipantData }
              : participant
        ),
      };
    },
    deleteParticipant: (state, action: PayloadAction<string>) => {
      const terminatedParticipants = state.terminatedParticipants.filter(
        (participant) => participant.nodeId !== action.payload
      );
      let earliestTerminationDate = state.form.terminationDate;
      if (terminatedParticipants.length < 1) {
        earliestTerminationDate = initialState.form.terminationDate;
      }
      return {
        ...state,
        terminatedParticipants,
        form: { ...state.form, terminationDate: earliestTerminationDate },
      };
    },
    setEarliestTerminationDate: (state) => {
      const earliestTerminationDate = Object.values(
        state.terminatedParticipants
      )
        .filter((participant) => participant.noticeDate) // Filter out empty dates
        .sort(
          (a, b) =>
            new Date(a.noticeDate).getTime() - new Date(b.noticeDate).getTime()
        ) // Sort by date
        .shift(); // Get the first item in the sorted array
      state.form.terminationDate.validateOnSubmit = false;
      state.form.terminationDate.value =
        earliestTerminationDate?.noticeDate ?? '';
    },
    clearTerminatedPersonFields: (state) => {
      const {
        personPersonalnumber,
        personNoticeDate,
        personAddress,
        personCity,
        personEmail,
        personEmployedFrom,
        personEmployedTo,
        personFirstname,
        personLastname,
        personTelephone,
        personZipCode,
      } = state.form;

      const fieldNames = [
        personPersonalnumber.name,
        personAddress.name,
        personCity.name,
        personEmail.name,
        personTelephone.name,
        personEmployedFrom.name,
        personEmployedTo.name,
        personNoticeDate.name,
        personFirstname.name,
        personLastname.name,
        personZipCode.name,
      ] as string[];

      fieldNames.forEach((fieldName) => {
        state.form = {
          ...state.form,
          [fieldName]: {
            ...initialState.form[fieldName as keyof AdjustmentAidFormValues],
          },
        };
      });
    },
    addTerminatedParticipantsFile: (
      state,
      action: PayloadAction<ApplicationFile>
    ) => {
      return {
        ...state,
        fileTerminatedParticipants: action.payload,
        terminatedParticipants: initialState.terminatedParticipants,
      };
    },
    deleteTerminatedParticipantsFile: (state) => {
      return {
        ...state,
        fileTerminatedParticipants: initialState.fileTerminatedParticipants,
        form: {
          ...state.form,
          templateFile: initialState.form.templateFile,
          terminationDate: initialState.form.terminationDate,
        },
      };
    },
    setMyRoleBool: (state, action: PayloadAction<AdjustmentAidMyRoleType>) => {
      return {
        ...state,
        myRole: action.payload === AdjustmentAidMyRoleType.CompanyRep,
      };
    },
    setUnionRoleBool: (
      state,
      action: PayloadAction<AdjustmentAidUnionRoleType>
    ) => {
      return {
        ...state,
        unionRole: action.payload === AdjustmentAidUnionRoleType.UnionDelegate,
      };
    },
    setMoreApplicationsBool: (
      state,
      action: PayloadAction<MoreApplications>
    ) => {
      return {
        ...state,
        moreApplications: action.payload === MoreApplications.MoreApplications,
      };
    },
    setUnion: (state, action: PayloadAction<DropdownAlternative>) => {
      return {
        ...state,
        union: { id: action.payload.id, name: action.payload.value },
      };
    },
    setApplicationFile: (state, action: PayloadAction<ApplicationFile>) => {
      return { ...state, fileTerminatedParticipants: action.payload };
    },
    setExtraInfo: (state, action: PayloadAction<string>) => {
      return { ...state, extraInfo: action.payload };
    },
    validateFormForSubmitAdjustmentAid: (state) => {
      const { applicationType } = state;
      if (applicationType) {
        const formFieldsToValidate = Object.values(state.form).filter(
          (field) => {
            return (
              (field.validateOnSubmit &&
                field.applicationTypes.includes(applicationType) &&
                !field.isValid) ||
              (field.isDirty &&
                !field.isValid &&
                field.applicationTypes.includes(applicationType))
            );
          }
        );

        formFieldsToValidate.forEach((field) => {
          field.isDirty = true;
          field.isValid = validateField(field);
        });

        if (
          (state.terminatedParticipants.length < +state.form.amountDismissed &&
            !state.fileTerminatedParticipants) ||
          (state.form.amountDismissed.isDirty &&
            !state.form.amountDismissed.isValid)
        ) {
          state.showErrorMessageMissingFileOrParticipants = true;
        } else {
          state.showErrorMessageMissingFileOrParticipants = false;
        }
      }
    },
    validateUnionAndCompanyContactEmail: (state) => {
      const { form } = state;
      const companyEmail = form.companyContactEmail;
      const unionEmail = form.unionContactEmail;

      if (companyEmail.isDirty && unionEmail.isDirty) {
        if (
          companyEmail.value.toLowerCase() === unionEmail.value.toLowerCase()
        ) {
          companyEmail.isValid = false;
          unionEmail.isValid = false;
          state.isSameEmailForContacts = true;
        } else {
          state.isSameEmailForContacts = false;
          companyEmail.isValid = validateField(companyEmail);
          unionEmail.isValid = validateField(unionEmail);
        }
      }
    },
    validateRegisteredParticipantSSN: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      const { form } = state;
      const ssnField = form.personPersonalnumber;
      let isExistingSSN = state.terminatedParticipants.some(
        (participant) =>
          participant.contact.socialSecurityNumber === ssnField.value
      );

      if (action.payload) {
        isExistingSSN = state.terminatedParticipants.some(
          (participant) =>
            participant.contact.socialSecurityNumber === ssnField.value &&
            participant.nodeId !== action.payload
        );
      }
      if (isExistingSSN) {
        ssnField.isValid = false;
        state.showErrorMessageExistingSSN = true;
      } else {
        state.showErrorMessageExistingSSN = false;
      }
    },
  },
});

export const {
  resetExistingAutomatedBankruptcyFlowState,
  setStateApplicationType,
  validateAndSetFormValueAdjustmentAid,
  setCheckingExistingAutomatedBankruptcyIncident,
  setExistingAutomatedBankruptcyIncidentResult,
  setExistingAutomatedBankruptcyIncidentLookupFailed,
  setExistingAutomatedBankruptcyIncidentFlowSuccess,
  setExistingAutomatedBankruptcyIncidentFlowSubmitting,
  setWorksite,
  setFullTexts,
  addTerminatedParticipant,
  clearTerminatedPersonFields,
  saveEditedParticipant,
  addTerminatedParticipantsFile,
  deleteTerminatedParticipantsFile,
  setMyRoleBool,
  setUnion,
  setUnionRoleBool,
  setMoreApplicationsBool,
  setApplicationFile,
  setExtraInfo,
  deleteParticipant,
  validateFormForSubmitAdjustmentAid,
  clearDidNotFindWorksiteFields,
  validateUnionAndCompanyContactEmail,
  validateRegisteredParticipantSSN,
  setEarliestTerminationDate,
} = adjustmentAidSlice.actions;

export default adjustmentAidSlice.reducer;
